/**
 * Mock Payments Console — superadmin red panel (Phase 3).
 *
 * Acts as a temporary stand-in for Stripe Dashboard. Admin can:
 *   - See current payment gateway (mock | stripe) and switch between them
 *   - Per subscription, simulate any lifecycle event (subscribed, renewed,
 *     payment_failed, recover_from_grace, block, trial_ended, cancelled)
 *   - Reset a subscription back to pending (test reset)
 *   - View recent mock-tagged events
 *
 * The system uses this in mock mode (default) until STRIPE_* env vars are
 * configured and admin switches gateway to 'stripe'.
 */

import { useCallback, useEffect, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import { superadminAPI } from '../../services/api'
import {
  HiOutlineCreditCard,
  HiOutlineArrowPath,
  HiOutlineExclamationTriangle,
  HiOutlineCheckCircle,
  HiOutlineXCircle,
  HiOutlineBeaker,
  HiOutlineEye,
  HiOutlinePlay,
  HiOutlineArrowsRightLeft,
} from 'react-icons/hi2'

const STATUS_STYLES = {
  trial:         'bg-info-soft text-info',
  active:        'bg-green-100 text-green-800',
  past_due:      'bg-amber-100 text-amber-800',
  blocked:       'bg-red-100 text-red-800',
  cancelled:     'bg-gray-100 text-gray-700',
  expired:       'bg-gray-100 text-gray-700',
  grandfathered: 'bg-indigo-100 text-indigo-800',
  pending:       'bg-gray-100 text-gray-700',
}

export default function MockPaymentsConsolePage() {
  const navigate = useNavigate()
  const [data, setData] = useState(null)
  const [loading, setLoading] = useState(true)
  const [busyId, setBusyId] = useState(null)
  const [search, setSearch] = useState('')
  const [statusFilter, setStatusFilter] = useState('')
  const [showOnlyMockPending, setShowOnlyMockPending] = useState(false)
  const [stripeConfigured, setStripeConfigured] = useState(false)
  const [gatewaySwitching, setGatewaySwitching] = useState(false)
  const [msg, setMsg] = useState('')

  const reload = useCallback(async () => {
    setLoading(true)
    try {
      const [consoleRes, gwRes] = await Promise.all([
        superadminAPI.getMockConsole(),
        superadminAPI.getPaymentGateway(),
      ])
      setData(consoleRes?.data?.data ?? consoleRes?.data ?? null)
      const gw = gwRes?.data?.data ?? gwRes?.data ?? {}
      setStripeConfigured(!!gw.stripe_configured)
    } finally {
      setLoading(false)
    }
  }, [])

  useEffect(() => { reload() }, [reload])

  const simulate = async (sub, eventKey, label) => {
    if (!window.confirm(`Simulate "${label}" for ${sub.account_name}?`)) return
    setBusyId(sub.id)
    setMsg('')
    try {
      const res = await superadminAPI.simulateSubscriptionEvent(sub.id, eventKey)
      if (res?.success) {
        setMsg(`✓ Simulated ${eventKey} for ${sub.account_name}`)
        await reload()
      } else {
        setMsg(`✗ ${res?.data?.message || 'Failed'}`)
      }
    } catch (e) {
      setMsg(`✗ ${e?.message || 'Network error'}`)
    } finally {
      setBusyId(null)
    }
  }

  const reset = async (sub) => {
    if (!window.confirm(`Reset subscription #${sub.id} (${sub.account_name}) back to 'pending'? This wipes trial/active state for re-testing.`)) return
    setBusyId(sub.id)
    setMsg('')
    try {
      const res = await superadminAPI.resetMockSubscription(sub.id)
      if (res?.success) {
        setMsg(`✓ Reset #${sub.id} to pending`)
        await reload()
      } else {
        setMsg(`✗ ${res?.data?.message || 'Failed'}`)
      }
    } catch (e) {
      setMsg(`✗ ${e?.message || 'Network error'}`)
    } finally {
      setBusyId(null)
    }
  }

  const switchGateway = async (newGateway) => {
    if (newGateway === data?.current_gateway) return
    if (newGateway === 'stripe' && !stripeConfigured) {
      setMsg('✗ STRIPE_SECRET_KEY / STRIPE_WEBHOOK_SECRET are not set. Configure them on the server first.')
      return
    }
    const confirm = newGateway === 'stripe'
      ? 'Switch to real Stripe? All future subscribes/cancels/refunds will hit Stripe with real cards.'
      : 'Switch back to mock? Stripe will be skipped — all payments will use the testing console.'
    if (!window.confirm(confirm)) return
    setGatewaySwitching(true)
    setMsg('')
    try {
      const res = await superadminAPI.setPaymentGateway(newGateway)
      if (res?.success) {
        setMsg(`✓ Payment gateway is now '${newGateway}'`)
        await reload()
      } else {
        setMsg(`✗ ${res?.data?.message || 'Failed'}`)
      }
    } catch (e) {
      setMsg(`✗ ${e?.message || 'Network error'}`)
    } finally {
      setGatewaySwitching(false)
    }
  }

  if (loading && !data) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="w-12 h-12 border-4 border-red-500 border-t-transparent rounded-full animate-spin" />
      </div>
    )
  }

  const subs = (data?.subscriptions || []).filter(s => {
    if (statusFilter && s.status !== statusFilter) return false
    if (showOnlyMockPending && !s.is_mock_pending_checkout) return false
    if (search && !s.account_name?.toLowerCase().includes(search.toLowerCase())) return false
    return true
  })

  const events = data?.recent_mock_events || []
  const currentGw = data?.current_gateway || 'mock'

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center gap-2">
            <HiOutlineBeaker className="w-6 h-6 text-danger" />
            Mock Payments Console
          </h1>
          <p className="text-gray-600 text-sm mt-1">
            Temporary payment-gateway simulator while Stripe is not configured.
            Use it to test the full subscription lifecycle end to end.
          </p>
        </div>
        <button onClick={reload} disabled={loading}
          className="px-4 py-2 bg-red-600 text-white rounded-lg hover:bg-red-700 disabled:opacity-60 flex items-center gap-2">
          <HiOutlineArrowPath className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
          Refresh
        </button>
      </div>

      {msg && (
        <div className={`p-3 rounded text-sm ${msg.startsWith('✓')
          ? 'bg-success-soft text-green-900 border border-success/30'
          : 'bg-danger-soft text-red-900 border border-danger/30'}`}>
          {msg}
        </div>
      )}

      {/* Gateway selector */}
      <div className="bg-white rounded-lg shadow-sm p-5">
        <div className="flex items-start justify-between gap-4 flex-wrap">
          <div>
            <h2 className="text-base font-semibold text-gray-900 flex items-center gap-2">
              <HiOutlineArrowsRightLeft className="w-5 h-5 text-danger" />
              Active payment gateway
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              The system currently uses{' '}
              <strong className={currentGw === 'stripe' ? 'text-success' : 'text-warning'}>
                {currentGw === 'stripe' ? 'Stripe (real)' : 'Mock (testing)'}
              </strong>
              {' '}for all subscribe/cancel/refund flows.
            </p>
          </div>
          <div className="flex gap-2">
            <button
              onClick={() => switchGateway('mock')}
              disabled={gatewaySwitching || currentGw === 'mock'}
              className={`px-4 py-2 text-sm font-medium rounded border-2 transition-all ${
                currentGw === 'mock'
                  ? 'bg-amber-100 border-amber-500 text-amber-900 cursor-default'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
            >
              {currentGw === 'mock' && '✓ '}Mock (testing)
            </button>
            <button
              onClick={() => switchGateway('stripe')}
              disabled={gatewaySwitching || currentGw === 'stripe'}
              className={`px-4 py-2 text-sm font-medium rounded border-2 transition-all ${
                currentGw === 'stripe'
                  ? 'bg-green-100 border-green-500 text-green-900 cursor-default'
                  : 'border-gray-300 bg-white text-gray-700 hover:bg-gray-50'
              }`}
              title={!stripeConfigured ? 'STRIPE_SECRET_KEY / STRIPE_WEBHOOK_SECRET must be configured first.' : ''}
            >
              {currentGw === 'stripe' && '✓ '}
              Stripe (real)
              {!stripeConfigured && <span className="ml-1 text-xs">⚠</span>}
            </button>
          </div>
        </div>
        {!stripeConfigured && (
          <div className="mt-3 p-3 bg-warning-soft border border-warning/30 rounded text-xs text-amber-900 flex items-start gap-2">
            <HiOutlineExclamationTriangle className="w-4 h-4 flex-shrink-0 mt-0.5" />
            <span>
              <strong>Stripe not configured:</strong> Set <code className="bg-white px-1 rounded">STRIPE_SECRET_KEY</code>,
              {' '}<code className="bg-white px-1 rounded">STRIPE_PUBLISHABLE_KEY</code>, and
              {' '}<code className="bg-white px-1 rounded">STRIPE_WEBHOOK_SECRET</code> on the server, then return here to switch over.
            </span>
          </div>
        )}
      </div>

      {/* Filters */}
      <div className="bg-white rounded-lg shadow-sm p-4 flex flex-wrap items-center gap-3">
        <input
          type="text"
          placeholder="Search workspace name"
          value={search}
          onChange={(e) => setSearch(e.target.value)}
          className="flex-1 min-w-[180px] border border-gray-300 rounded px-3 py-1.5 text-sm"
        />
        <select value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}
          className="border border-gray-300 rounded px-2 py-1.5 text-sm">
          <option value="">All statuses</option>
          <option value="pending">pending</option>
          <option value="trial">trial</option>
          <option value="active">active</option>
          <option value="past_due">past_due</option>
          <option value="blocked">blocked</option>
          <option value="cancelled">cancelled</option>
          <option value="expired">expired</option>
          <option value="grandfathered">grandfathered</option>
        </select>
        <label className="text-sm text-gray-700 flex items-center gap-2 cursor-pointer">
          <input type="checkbox" checked={showOnlyMockPending} onChange={(e) => setShowOnlyMockPending(e.target.checked)} />
          Pending mock checkout only
        </label>
        <span className="text-sm text-gray-500 ml-auto">{subs.length} of {data?.subscriptions?.length ?? 0}</span>
      </div>

      {/* Subscriptions table */}
      <div className="bg-white rounded-lg shadow-sm overflow-x-auto">
        <table className="min-w-full text-sm">
          <thead className="bg-gray-50">
            <tr className="text-left text-gray-700 text-xs uppercase tracking-wide">
              <th className="px-4 py-3">Workspace</th>
              <th className="px-4 py-3">Status</th>
              <th className="px-4 py-3">Plan</th>
              <th className="px-4 py-3">Dates</th>
              <th className="px-4 py-3">Simulate event</th>
              <th className="px-4 py-3"></th>
            </tr>
          </thead>
          <tbody className="divide-y divide-gray-100">
            {subs.map(sub => (
              <tr key={sub.id} className="hover:bg-gray-50">
                <td className="px-4 py-3">
                  <div className="font-medium text-gray-900">{sub.account_name}</div>
                  <div className="text-xs text-gray-500">sub #{sub.id} · ${sub.current_price_usd}/yr</div>
                  {sub.is_mock_pending_checkout && (
                    <span className="inline-block mt-1 text-[10px] font-bold uppercase bg-amber-200 text-amber-900 px-2 py-0.5 rounded">
                      mock checkout pending
                    </span>
                  )}
                </td>
                <td className="px-4 py-3">
                  <span className={`text-[10px] font-bold uppercase px-2 py-0.5 rounded ${STATUS_STYLES[sub.status] || 'bg-gray-100 text-gray-700'}`}>
                    {sub.status}
                  </span>
                </td>
                <td className="px-4 py-3 text-gray-700">{sub.plan || '—'}</td>
                <td className="px-4 py-3 text-gray-600 text-xs leading-relaxed">
                  {sub.trial_ends_at && <div>Trial: {new Date(sub.trial_ends_at).toLocaleDateString()}</div>}
                  {sub.current_period_end && <div>Renews: {new Date(sub.current_period_end).toLocaleDateString()}</div>}
                  {sub.grace_period_ends_at && <div className="text-warning">Grace: {new Date(sub.grace_period_ends_at).toLocaleDateString()}</div>}
                  {!sub.trial_ends_at && !sub.current_period_end && !sub.grace_period_ends_at && '—'}
                </td>
                <td className="px-4 py-3">
                  {sub.available_events?.length > 0 ? (
                    <div className="flex flex-wrap gap-1">
                      {sub.available_events.map(ev => (
                        <button
                          key={ev.key}
                          type="button"
                          onClick={() => simulate(sub, ev.key, ev.label)}
                          disabled={busyId === sub.id}
                          className="inline-flex items-center gap-1 px-2 py-1 text-[11px] font-medium rounded bg-info-soft text-brand border border-info/30 hover:bg-blue-100 disabled:opacity-50"
                        >
                          <HiOutlinePlay className="w-3 h-3" />
                          {ev.label}
                        </button>
                      ))}
                    </div>
                  ) : (
                    <span className="text-xs text-gray-400">No events available for this state</span>
                  )}
                </td>
                <td className="px-4 py-3 text-right">
                  <div className="flex justify-end gap-2">
                    <button
                      onClick={() => navigate(`/superadmin/subscriptions/${sub.id}`)}
                      className="text-danger hover:text-red-800 inline-flex items-center gap-1 text-xs"
                    >
                      <HiOutlineEye className="w-3.5 h-3.5" /> View
                    </button>
                    {sub.status !== 'grandfathered' && (
                      <button
                        onClick={() => reset(sub)}
                        disabled={busyId === sub.id}
                        className="text-gray-600 hover:text-gray-800 inline-flex items-center gap-1 text-xs disabled:opacity-50"
                        title="Test reset (wipe trial/active state)"
                      >
                        <HiOutlineArrowPath className="w-3.5 h-3.5" /> Reset
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ))}
            {subs.length === 0 && (
              <tr><td colSpan={6} className="px-4 py-8 text-center text-sm text-gray-500">No subscriptions match your filters.</td></tr>
            )}
          </tbody>
        </table>
      </div>

      {/* Recent mock events */}
      <div className="bg-white rounded-lg shadow-sm p-5">
        <h2 className="text-base font-semibold text-gray-900 mb-3 flex items-center gap-2">
          <HiOutlineCheckCircle className="w-5 h-5 text-danger" />
          Recent mock transactions ({events.length})
        </h2>
        {events.length === 0 ? (
          <p className="text-sm text-gray-500">No mock-tagged events yet.</p>
        ) : (
          <div className="divide-y divide-gray-100">
            {events.map(ev => (
              <div key={ev.id} className="py-2.5 flex items-center justify-between text-sm">
                <div className="flex-1 min-w-0">
                  <div className="font-medium text-gray-900">
                    {ev.event_type}
                    <span className="text-xs text-gray-500 ml-2">on {ev.subscription__account__name}</span>
                  </div>
                  {ev.actor__name && (
                    <div className="text-xs text-gray-500">by {ev.actor__name}</div>
                  )}
                </div>
                <span className="text-xs text-gray-400 flex-shrink-0">{new Date(ev.created_at).toLocaleString()}</span>
              </div>
            ))}
          </div>
        )}
      </div>

      {/* Help footer */}
      <div className="bg-info-soft border border-info/30 rounded-lg p-4 text-sm text-blue-900">
        <h3 className="font-semibold mb-1">How to use this page</h3>
        <ol className="list-decimal list-inside space-y-1 text-blue-800">
          <li>While Stripe isn't configured, the system uses <strong>mock mode</strong>: when a user clicks "Subscribe", they land on a fake payment page where they pick success or failure.</li>
          <li>From here, you can also simulate any event manually (renewal, payment failure, grace recovery, block, cancel) — useful for testing the full lifecycle without waiting for real dates to pass.</li>
          <li>Use <strong>Reset</strong> to wipe a subscription back to pending so you can re-test from scratch with the same workspace.</li>
          <li>When Stripe is ready, set the env vars and click <strong>Stripe (real)</strong> above. Existing subscriptions stay on their current state — only future actions hit Stripe.</li>
        </ol>
      </div>
    </div>
  )
}
